import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import { push } from 'connected-react-router';

import { selectMenu, selectMenuCategoriesEntities } from 'rdx/menu';
import { selectQuery } from 'rdx/router';
import { getLocalisedStringValue } from 'utils/locales';

import { useCommonAppServices } from 'root/CommonAppServicesContext';

interface MenuCategoryProps {
  id: number | 'options';
}

const MenuCategory: React.FC<MenuCategoryProps> = ({ id }) => {
  const dispatch = useDispatch();
  const { trackingService } = useCommonAppServices();
  const intl = useIntl();
  const menu = useSelector(selectMenu);
  const categories = useSelector(selectMenuCategoriesEntities);
  const { selectedCategoryId, showSoldOutItems, search } = useSelector(selectQuery);
  const category = id !== 'options' && categories[id];
  // we do not mark category as active, if we in filter mode
  const isActive = showSoldOutItems !== 'true' && !search && selectedCategoryId === String(id);

  const name =
    id !== 'options' && category
      ? getLocalisedStringValue(category.name)
      : intl.formatMessage({ id: 'widgets.MenuEditor.options' });
  const total = id !== 'options' && category ? category.dishes.length : menu.result.options.length;

  const handleSelect = useCallback(() => {
    dispatch(push({ search: `?selectedCategoryId=${id}` }));
    if (id !== 'options') {
      trackingService.pushMenuCategoryChangeEvent({ id, name: name! });
    }
  }, [trackingService, dispatch, id, name]);

  if (!category && id !== 'options') {
    return null;
  }

  return (
    <Box
      display="flex"
      onClick={handleSelect}
      alignItems="center"
      mb={1}
      sx={{ cursor: 'pointer' }}
    >
      <Box
        textAlign="center"
        px={1}
        py={0.5}
        mr={1}
        sx={{ backgroundColor: 'colors.neutral200', minWidth: '16px' }}
      >
        <Typography variant="bodyM" sx={{ color: 'colors.neutral700' }}>
          {total}
        </Typography>
      </Box>
      <Box>
        <Typography variant="bodyM" color={isActive ? 'primary' : 'default'}>
          {name}
        </Typography>
      </Box>
    </Box>
  );
};

export default MenuCategory;
